/**
 * Ported from the Atlas repo (boomerang):
 * https://github.com/penske-media-corp/boomerang/blob/main/resources/atlasmg/src/Utils/ImportUtils.ts
 *
 * This module contains tooling to make dynamic importing better/easier to use
 * when coupled with our Webpack bundler. Webpack uses static code analysis to
 * determine what module(s) is/are being imported from an import() statement.
 * For that reason, the location in the code MUST contain the import()
 * directive, and the string passed to import() MUST be a static string.
 * Variables are not allowed, and string interpolation in the import() call can
 * lead to some unexpected results, such as bundling of more modules than
 * expected within the dynamic chunk.
 *
 * Each function takes the Promise returned from import() and wraps it to
 * provide error logging/handling.  On failure, an error will be logged.  The
 * second parameter to each function takes the name of the module for logging
 * purposes.  Due to the static code analysis performed by Webpack, we can't
 * simply pass in the module path and reuse that for logging, so you must pass
 * the module description in this second parameter.  If you need extra error
 * handling from within your caller, you can use the `getImportWithRethrowThen`
 * function, and any failures will be propagated through the returned Promise.
 *
 * The last parameter to each function is the onFulfilled function to call when
 * the import() Promise succeeds.  This function is the same function you would
 * pass to the .then() call (ie import('ModuleName').then(onFulfilled)).  This
 * function will ONLY be called if the import() promise succeeds, so you don't
 * have to worry about catching errors in that function.
 *
 * Each function returns a Promise that can be used by the caller.  On success,
 * the returned Promise will be the result of the onFulfilled function passed
 * in through the third parameter.  On import failure, the Promise will still
 * fulfill, but it will fulfill with an empty value (undefined).  In this way,
 * the resulting Promise can be used to determine when the import is complete,
 * either successfully or not.  In the case of the `getImportWithRethrowThen`
 * function, the returned Promise will be rejected if the import() fails.  The
 * caller will need to `.catch()` the resulting rejection and handle it.
 * Otherwise, like the other functions, it will return a Promise that fulfills
 * with the result of the onFulfilled function when the import succeeds.
 *
 * There is a convenience function, `getImportWithDefaultExportThen` that
 * unwraps the `default` export for modules that only contain a default export
 * rather than requiring that the caller unwrap that itself.  Other than this,
 * it is identical to `getImportThen`.
 *
 * Examples:
 *
 * getImportThen(
 *     import('./DynamicModule'),
 *     'DynamicModule',
 *     ({ exportedFunction }) => {
 *         exportedFunction();
 *     }
 * );
 *
 * getImportWithDefaultExportThen(
 *     import('./DynamicModule'),
 *     'DynamicModule',
 *     (defaultExport) => {
 *         defaultExport();
 *     }
 * );
 *
 * getImportWithRethrowThen(
 *     import('./DynamicModule'),
 *     'DynamicModule',
 *     ({ exportedFunction }) => {
 *         exportedFunction();
 *     }
 * ).catch((error) => {
 *     // Do something with the error.
 * });
 */
import Logger from './logger';

/**
 * Log an error encountered while dynamically importing a module.
 *
 * @param {string} moduleDescription A human readable name for the module.
 * @param {*} error The error thrown by the failed import().
 */
const logModuleLoadingError = (moduleDescription, error) => {
  Logger.error(`Error loading module: ${moduleDescription}`, { error });
};

/**
 * Wrap a dynamic import() Promise with error logging.
 *
 * @param {Promise} importPromise The Promise returned by import().
 * @param {string} moduleDescription A human readable name for the module.
 * @param {Function} onFulfilled Called with the module exports on success.
 * @returns {Promise} Fulfills with the onFulfilled result, or undefined on failure.
 */
const getImportThen = (importPromise, moduleDescription, onFulfilled) =>
  importPromise.then(onFulfilled).catch(error => {
    logModuleLoadingError(moduleDescription, error);
  });

/**
 * Wrap a dynamic import() Promise with error logging, re-throwing on failure
 * so the caller can perform its own handling via .catch().
 *
 * @param {Promise} importPromise The Promise returned by import().
 * @param {string} moduleDescription A human readable name for the module.
 * @param {Function} onFulfilled Called with the module exports on success.
 * @returns {Promise} Fulfills with the onFulfilled result, or rejects on failure.
 */
const getImportWithRethrowThen = (importPromise, moduleDescription, onFulfilled) =>
  importPromise.then(onFulfilled).catch(error => {
    logModuleLoadingError(moduleDescription, error);

    throw error;
  });

/**
 * Like getImportThen, but unwraps the module's `default` export before calling
 * onFulfilled. Use for modules that only expose a default export.
 *
 * @param {Promise} importPromise The Promise returned by import().
 * @param {string} moduleDescription A human readable name for the module.
 * @param {Function} onFulfilled Called with the default export on success.
 * @returns {Promise} Fulfills with the onFulfilled result, or undefined on failure.
 */
const getImportWithDefaultExportThen = (importPromise, moduleDescription, onFulfilled) =>
  importPromise
    .then(moduleExports => moduleExports.default)
    .then(onFulfilled)
    .catch(error => {
      logModuleLoadingError(moduleDescription, error);
    });

export { getImportThen, getImportWithRethrowThen, getImportWithDefaultExportThen };
